/**
 * Login page — rediseño v2.1.
 *
 * Visual: fullscreen, wordmark hero "cm.champe" Plex Mono Bold con `.` magenta,
 * ambient gradient sutil, card central con primary SSO + dev login, theme
 * toggle en top-right, footer minimal.
 *
 * Lógica preservada del original:
 * - Producción: botón "Continuar con Google Workspace" → /api/auth/workos/authorize.
 * - Dev: input de email + POST a /api/auth/dev-login (sólo non-prod).
 * - Mostramos ambos siempre porque el front no sabe si WORKOS está configurado.
 *   El primero falla con 503 si WORKOS no está seteado; el segundo falla con
 *   404 en prod (route oculta).
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ThemeToggle } from "../components/ui/theme-toggle";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function onDevLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/dev-login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, role: "admin" }),
      });
      if (res.ok) {
        navigate({ to: "/" });
      } else {
        const json = (await res.json().catch(() => ({}))) as {
          error?: { message?: string };
        };
        alert(`Dev login no disponible: ${json.error?.message ?? res.status}`);
      }
    } finally {
      setLoading(false);
    }
  }

  function onSsoLogin() {
    window.location.assign("/api/auth/workos/authorize");
  }

  return (
    <main className="relative min-h-screen flex flex-col items-center justify-center bg-background text-text-primary overflow-hidden">
      {/* Ambient gradient — magenta-soft muy discreto */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 70% 30%, var(--magenta-soft-bg), transparent 70%), radial-gradient(ellipse 50% 40% at 20% 80%, var(--magenta-soft-bg), transparent 70%)",
        }}
      />

      {/* Top-right theme toggle */}
      <div className="absolute top-4 right-6 z-10">
        <ThemeToggle />
      </div>

      {/* Login card area */}
      <div className="relative z-10 w-full max-w-[440px] px-6">
        {/* Brand chip */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <span
            className="w-2.5 h-2.5 rounded-full bg-magenta"
            style={{ boxShadow: "0 0 12px rgba(255,0,105,0.45)" }}
          />
          <span className="text-sm font-medium text-text-secondary">
            champe agency
          </span>
        </div>

        {/* Hero wordmark */}
        <h1 className="font-mono font-bold text-center lowercase tracking-tight text-text-primary text-5xl mb-3">
          cm<span className="text-magenta">.</span>champe
        </h1>
        <p className="text-center text-md text-text-secondary mb-10">
          Moderación humana asistida para community managers.
        </p>

        {/* Card */}
        <div className="bg-surface-1 border border-border rounded-2xl shadow-md p-8 space-y-5">
          {/* Primary SSO */}
          <Button
            type="button"
            onClick={onSsoLogin}
            variant="primary"
            size="lg"
            className="w-full normal-case tracking-normal font-sans font-medium rounded-lg"
          >
            <GoogleIcon />
            <span>Continuar con Google Workspace</span>
          </Button>
          <p className="text-center text-sm text-text-muted">
            Solo cuentas{" "}
            <span className="font-mono text-text-secondary">
              @champe.agency
            </span>
          </p>

          {/* Separator with "O" */}
          <div className="flex items-center gap-4 py-1">
            <div className="flex-1 h-px bg-border-subtle" />
            <span className="font-mono uppercase text-xs text-text-muted tracking-wider">
              o
            </span>
            <div className="flex-1 h-px bg-border-subtle" />
          </div>

          {/* Dev login */}
          <form onSubmit={onDevLogin} className="space-y-3">
            <label className="block">
              <span className="block font-mono uppercase tracking-wider text-2xs text-text-muted mb-2">
                Dev login (solo no-prod)
              </span>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="marcos@champe.agency"
                className="h-10 rounded-md bg-surface-2 text-text-primary placeholder:text-text-muted border-border focus:border-magenta focus:shadow-focus"
              />
            </label>
            <Button
              type="submit"
              disabled={loading || !email}
              variant="secondary"
              size="md"
              className="w-full normal-case tracking-normal font-sans font-medium rounded-lg h-10 bg-surface-2 text-text-primary border-border hover:bg-surface-3"
            >
              {loading ? "Ingresando…" : "Continuar"}
            </Button>
          </form>
        </div>

        {/* Footer links */}
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-text-muted">
          <a href="#" className="hover:underline">
            privacy
          </a>
          <span className="text-text-disabled">·</span>
          <a href="#" className="hover:underline">
            terms
          </a>
          <span className="text-text-disabled">·</span>
          <span className="font-mono text-xs">by champe agency</span>
        </div>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M21.35 11.1h-9.17v2.96h5.27c-.23 1.46-1.7 4.28-5.27 4.28-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.81 0 3.02.77 3.71 1.43l2.53-2.43C16.78 4.16 14.7 3.2 12.18 3.2c-4.94 0-8.95 4.01-8.95 8.95s4.01 8.95 8.95 8.95c5.17 0 8.6-3.63 8.6-8.74 0-.59-.06-1.04-.13-1.49z" />
    </svg>
  );
}
